import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { authService, api } from '../../services/api';

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const UserManagement = () => {
  // Estados principais
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [userHotels, setUserHotels] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados de filtros
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    hotel: '',
    status: ''
  });
  
  // Estados de modais
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  
  // Estados de formulários
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'STAFF',
    hotelCode: ''
  });
  
  // Estados para seletor de hotel avançado
  const [hotelSearchTerm, setHotelSearchTerm] = useState('');
  const [showHotelDropdown, setShowHotelDropdown] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  
  // Debounce para busca de hotéis (performance)
  const debouncedSearchTerm = useDebounce(hotelSearchTerm, 300);
  
  // Ref para controlar dropdown
  const dropdownRef = useRef(null);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Estados de loading para operações específicas
  const [loadingStates, setLoadingStates] = useState({
    create: false,
    edit: false,
    delete: false
  });
  
  // Estados de notificação
  const [notificationData, setNotificationData] = useState({
    type: 'success',
    title: '',
    message: '',
    details: null
  });
  
  // Estado do hotel selecionado para o formulário
  const [selectedHotelForForm, setSelectedHotelForForm] = useState(null);
  
  // Estados para gestão de hotéis
  const [showHotelModal, setShowHotelModal] = useState(false);
  const [userForHotel, setUserForHotel] = useState(null);
  const [selectedHotelForUser, setSelectedHotelForUser] = useState('');
  const [isPrimaryHotel, setIsPrimaryHotel] = useState(false);
  const [loadingHotelOperation, setLoadingHotelOperation] = useState(false);

  // Estados para alteração de status
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [userForStatus, setUserForStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [loadingStatusOperation, setLoadingStatusOperation] = useState(false);

  // Estado do menu mobile
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigate = useNavigate();

  // Carregar hotel selecionado
  useEffect(() => {
    const storedHotel = localStorage.getItem('selectedHotel');
    if (storedHotel) {
      try {
        setSelectedHotel(JSON.parse(storedHotel));
      } catch (error) {
        console.error('Erro ao parsear hotel selecionado:', error);
      }
    }
  }, []);

  // Verificar autenticação e carregar dados
  useEffect(() => {
    const checkAuth = () => {
      const user = authService.getCurrentUser();
      if (!user || user.role !== 'ADMIN') {
        showNotificationModal('error', 'Acesso Negado', 'Apenas administradores podem acessar esta página.');
        navigate('/dashboard');
        return;
      }
      loadData();
    };

    checkAuth();
  }, [navigate]);

  // Aplicar filtros quando necessário
  useEffect(() => {
    applyFilters();
  }, [users, filters]);

  // Efeito para fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowHotelDropdown(false);
      }
    };

    if (showHotelDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHotelDropdown]);

  // Função para mostrar notificações
  const showNotificationModal = useCallback((type, title, message, details = null) => {
    setNotificationData({ type, title, message, details });
    setShowNotification(true);
  }, []);

  // Função para fechar notificação
  const handleCloseNotification = useCallback(() => {
    setShowNotification(false);
    setNotificationData({
      type: 'success',
      title: '',
      message: '',
      details: null
    });
  }, []);

  // Carregar dados com tratamento de erro
  const loadData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Carregar dados em paralelo
      const [usersResponse, hotelsResponse, userHotelsResponse] = await Promise.all([
        api.get('/users'),
        api.get('/hotels'),
        api.get('/user-access')
      ]);
      
      setUsers(usersResponse.data || []);
      setHotels(hotelsResponse.data || []);
      setUserHotels(userHotelsResponse.data || []);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      showNotificationModal('error', 'Erro ao Carregar', 'Erro ao carregar dados. Tente novamente.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [showNotificationModal]);

  // Aplicar filtros com validação
  const applyFilters = useCallback(() => {
    try {
      let filtered = [...users];

      // Filtrar por busca (sanitizar entrada)
      if (filters.search && typeof filters.search === 'string') {
        const searchTerm = filters.search.toLowerCase().replace(/[<>]/g, '');
        if (searchTerm.length > 0) {
          filtered = filtered.filter(user => 
            user.name?.toLowerCase().includes(searchTerm) ||
            user.email?.toLowerCase().includes(searchTerm)
          );
        }
      }

      // Filtrar por função
      if (filters.role && typeof filters.role === 'string') {
        filtered = filtered.filter(user => user.role === filters.role);
      }

      // Filtrar por hotel
      if (filters.hotel && typeof filters.hotel === 'string') {
        filtered = filtered.filter(user => {
          const userHotelRelations = userHotels.filter(uh => uh.userId === user.id);
          return userHotelRelations.some(relation => relation.hotelId === filters.hotel);
        });
      }

      setFilteredUsers(filtered);
    } catch (error) {
      console.error('Erro ao aplicar filtros:', error);
      setFilteredUsers(users);
    }
  }, [users, filters, userHotels]);

  // Limpar filtros
  const handleClearFilters = useCallback(() => {
    setFilters({
      search: '',
      role: '',
      hotel: '',
      status: ''
    });
  }, []);

  // Validar dados do usuário
  const validateUserData = useCallback((userData, isEdit = false) => {
    const errors = [];

    // Validar nome
    if (!userData.name || typeof userData.name !== 'string' || userData.name.trim().length < 2) {
      errors.push('Nome deve ter pelo menos 2 caracteres');
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userData.email || !emailRegex.test(userData.email)) {
      errors.push('Email deve ser válido');
    }

    // Validar senha (apenas para criação)
    if (!isEdit && (!userData.password || userData.password.length < 6)) {
      errors.push('Senha deve ter pelo menos 6 caracteres');
    }

    // Validar função
    const validRoles = ['STAFF', 'MANAGER', 'ADMIN'];
    if (!userData.role || !validRoles.includes(userData.role)) {
      errors.push('Função deve ser válida');
    }

    // Validar código do hotel (obrigatório para criação)
    if (!isEdit && (!userData.hotelCode || typeof userData.hotelCode !== 'string' || userData.hotelCode.trim().length === 0)) {
      errors.push('Código do hotel é obrigatório');
    }

    return errors;
  }, []);

  // Sanitizar dados do usuário
  const sanitizeUserData = useCallback((userData) => {
    return {
      name: userData.name?.replace(/[<>]/g, '').trim(),
      email: userData.email?.replace(/[<>]/g, '').trim().toLowerCase(),
      password: userData.password,
      role: userData.role,
      hotelCode: userData.hotelCode
    };
  }, []);

  // Criar usuário com validação
  const handleCreateUser = useCallback(async () => {
    try {
      setLoadingStates(prev => ({ ...prev, create: true }));
      
      // Sanitizar dados
      const sanitizedData = sanitizeUserData(newUser);
      
      // Validar dados
      const errors = validateUserData(sanitizedData, false);
      if (errors.length > 0) {
        showNotificationModal('error', 'Erro de Validação', errors.join(', '));
      return;
    }

      const response = await api.post('/auth/register', sanitizedData);
      
      showNotificationModal('success', 'Usuário Criado!', `Usuário ${response.data?.name || sanitizedData.name} criado com sucesso! A lista foi atualizada.`);
      
      // Limpar formulário
      setNewUser({ name: '', email: '', password: '', role: 'STAFF', hotelCode: '' });
      setHotelSearchTerm('');
      setSelectedHotelForForm(null);
      setShowHotelDropdown(false);
      setShowCreateModal(false);
      
      // Resetar filtros para mostrar o novo usuário
      setFilters({
        search: '',
        role: '',
        hotel: '',
        status: ''
      });
      
      // Recarregar dados com loading visível
      await loadData(true);
      
      // Scroll para o topo da lista para mostrar o novo usuário
      setTimeout(() => {
        const userList = document.querySelector('[data-testid="user-list"]');
        if (userList) {
          userList.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Destacar o novo usuário na lista (opcional)
          const newUserRow = userList.querySelector(`tr[data-user-id="${response.data?.id}"]`);
          if (newUserRow) {
            newUserRow.classList.add('bg-primary/5', 'border-primary/20');
            setTimeout(() => {
              newUserRow.classList.remove('bg-primary/5', 'border-primary/20');
            }, 3000);
          }
        }
      }, 500);
      
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      
      let errorMessage = 'Erro ao criar usuário. Tente novamente.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        errorMessage = 'Erro de validação nos dados';
      }
      
      showNotificationModal('error', 'Erro ao Criar', errorMessage);
    } finally {
      setLoadingStates(prev => ({ ...prev, create: false }));
    }
  }, [newUser, sanitizeUserData, validateUserData, showNotificationModal, loadData]);

  // Editar usuário com validação
  const handleEditUser = useCallback((user) => {
    setEditingUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      password: ''
    });
    setShowEditModal(true);
  }, []);

  // Atualizar usuário com validação
  const handleUpdateUser = useCallback(async () => {
    if (!editingUser) return;

    try {
      setLoadingStates(prev => ({ ...prev, edit: true }));
      
      // Sanitizar dados
      const sanitizedData = sanitizeUserData(editingUser);
      
      // Validar dados
      const errors = validateUserData(sanitizedData, true);
      if (errors.length > 0) {
        showNotificationModal('error', 'Erro de Validação', errors.join(', '));
      return;
    }

      const updateData = {
        name: sanitizedData.name,
        email: sanitizedData.email,
        role: sanitizedData.role
      };

      // Só incluir senha se foi preenchida
      if (sanitizedData.password && sanitizedData.password.length > 0) {
        updateData.password = sanitizedData.password;
      }

      await api.put(`/users/${editingUser.id}`, updateData);
      
      showNotificationModal('success', 'Usuário Atualizado!', `Usuário ${sanitizedData.name} atualizado com sucesso!`);
      
      setEditingUser(null);
      setShowEditModal(false);
      
      // Recarregar dados
      await loadData(false);
      
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      
      let errorMessage = 'Erro ao atualizar usuário. Tente novamente.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showNotificationModal('error', 'Erro ao Atualizar', errorMessage);
    } finally {
      setLoadingStates(prev => ({ ...prev, edit: false }));
    }
  }, [editingUser, sanitizeUserData, validateUserData, showNotificationModal, loadData]);

  // Excluir usuário com confirmação
  const handleDeleteUser = useCallback((user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  }, []);

  // Confirmar exclusão
  const confirmDeleteUser = useCallback(async () => {
    if (!userToDelete) return;

    try {
      setLoadingStates(prev => ({ ...prev, delete: true }));
      
      await api.delete(`/users/${userToDelete.id}`);
      
      showNotificationModal('success', 'Usuário Excluído!', `Usuário ${userToDelete.name} excluído com sucesso!`);
      
      setUserToDelete(null);
      setShowDeleteModal(false);
      
      // Recarregar dados
      await loadData(false);
      
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      
      let errorMessage = 'Erro ao excluir usuário. Tente novamente.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showNotificationModal('error', 'Erro ao Excluir', errorMessage);
    } finally {
      setLoadingStates(prev => ({ ...prev, delete: false }));
    }
  }, [userToDelete, showNotificationModal, loadData]);

  // Cancelar exclusão
  const handleCancelDelete = useCallback(() => {
    setUserToDelete(null);
    setShowDeleteModal(false);
  }, []);

  // Handlers para filtros
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Funções para seletor de hotel avançado com validações de segurança
  const filteredHotels = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return hotels.slice(0, 10); // Limitar resultados para performance
    
    // Sanitizar termo de busca para segurança
    const sanitizedTerm = debouncedSearchTerm.toLowerCase().replace(/[<>]/g, '');
    if (sanitizedTerm.length < 2) return hotels.slice(0, 10);
    
    return hotels.filter(hotel => {
      // Validações de segurança - garantir que os campos existem
      if (!hotel || typeof hotel !== 'object') return false;
      
      const name = hotel.name?.toLowerCase() || '';
      const code = hotel.code?.toLowerCase() || '';
      const city = hotel.city?.toLowerCase() || '';
      const state = hotel.state?.toLowerCase() || '';
      
      return name.includes(sanitizedTerm) ||
             code.includes(sanitizedTerm) ||
             city.includes(sanitizedTerm) ||
             state.includes(sanitizedTerm);
    }).slice(0, 20); // Limitar a 20 resultados para performance
  }, [hotels, debouncedSearchTerm]);

  const handleHotelSelect = useCallback((hotel) => {
    // Validação de segurança
    if (!hotel || !hotel.id || !hotel.code || !hotel.name) {
      console.error('Hotel inválido selecionado:', hotel);
      return;
    }
    
    setSelectedHotelForForm(hotel);
    setNewUser(prev => ({ ...prev, hotelCode: hotel.code }));
    setHotelSearchTerm(`${hotel.name} - ${hotel.code}`);
    setShowHotelDropdown(false);
  }, []);

  const handleHotelSearchChange = useCallback((value) => {
    // Sanitizar entrada para segurança
    const sanitizedValue = value.replace(/[<>]/g, '').slice(0, 100); // Limitar tamanho
    
    setHotelSearchTerm(sanitizedValue);
    setShowHotelDropdown(true);
    
    // Se limpar o campo, limpar seleção
    if (!sanitizedValue.trim()) {
      setSelectedHotelForForm(null);
      setNewUser(prev => ({ ...prev, hotelCode: '' }));
    }
  }, []);

  // Handlers para modais
  const handleCreateModal = useCallback(() => {
    setNewUser({ name: '', email: '', password: '', role: 'STAFF', hotelCode: '' });
    setHotelSearchTerm('');
    setSelectedHotelForForm(null);
    setShowHotelDropdown(false);
    setShowCreateModal(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setNewUser({ name: '', email: '', password: '', role: 'STAFF', hotelCode: '' });
    setHotelSearchTerm('');
    setSelectedHotelForForm(null);
    setShowHotelDropdown(false);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditingUser(null);
  }, []);

  // Handlers para gestão de hotéis
  const handleManageHotels = useCallback((user) => {
    setUserForHotel(user);
    setSelectedHotelForUser('');
    setIsPrimaryHotel(false);
    setShowHotelModal(true);
  }, []);

  const handleAddUserToHotel = useCallback(async () => {
    if (!userForHotel || !selectedHotelForUser) {
      showNotificationModal('error', 'Campos Obrigatórios', 'Selecione um usuário e um hotel.');
      return;
    }

    try {
      setLoadingHotelOperation(true);
      
      await api.post('/user-access', {
        userId: userForHotel.id,
        hotelId: selectedHotelForUser,
        isPrimary: isPrimaryHotel
      });

      showNotificationModal('success', 'Usuário Adicionado!', `Usuário ${userForHotel.name} adicionado ao hotel com sucesso!`);
      
      setShowHotelModal(false);
      setUserForHotel(null);
      setSelectedHotelForUser('');
      setIsPrimaryHotel(false);
      
      await loadData(false);
      
    } catch (error) {
      console.error('Erro ao adicionar usuário ao hotel:', error);
      
      let errorMessage = 'Erro ao adicionar usuário ao hotel. Tente novamente.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showNotificationModal('error', 'Erro ao Adicionar', errorMessage);
    } finally {
      setLoadingHotelOperation(false);
    }
  }, [userForHotel, selectedHotelForUser, isPrimaryHotel, showNotificationModal, loadData]);

  const handleRemoveUserFromHotel = useCallback(async (userId, hotelId) => {
    try {
      await api.delete(`/user-access/${userId}/${hotelId}`);
      
      const user = users.find(u => u.id === userId);
      const hotel = hotels.find(h => h.id === hotelId);
      
      showNotificationModal('success', 'Usuário Removido!', `Usuário ${user?.name} removido do hotel ${hotel?.name} com sucesso!`);
      
      await loadData(false);
      
    } catch (error) {
      console.error('Erro ao remover usuário do hotel:', error);
      
      let errorMessage = 'Erro ao remover usuário do hotel. Tente novamente.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showNotificationModal('error', 'Erro ao Remover', errorMessage);
    }
  }, [users, hotels, showNotificationModal, loadData]);

  const handleCloseHotelModal = useCallback(() => {
    setShowHotelModal(false);
    setUserForHotel(null);
    setSelectedHotelForUser('');
    setIsPrimaryHotel(false);
  }, []);

  // Handlers para alteração de status
  const handleChangeStatus = useCallback((user) => {
    setUserForStatus(user);
    setNewStatus(user.status === 'INACTIVE' ? 'ACTIVE' : 'INACTIVE');
    setShowStatusModal(true);
  }, []);

  const handleConfirmStatusChange = useCallback(async () => {
    if (!userForStatus) return;

    try {
      setLoadingStatusOperation(true);
      
      await api.put(`/users/${userForStatus.id}`, {
        status: newStatus
      });

      const statusText = newStatus === 'ACTIVE' ? 'Ativo' : 'Inativo';
      showNotificationModal('success', 'Status Alterado!', `Status do usuário ${userForStatus.name} alterado para ${statusText} com sucesso!`);
      
      setShowStatusModal(false);
      setUserForStatus(null);
      setNewStatus('');
      
      await loadData(false);
      
    } catch (error) {
      console.error('Erro ao alterar status do usuário:', error);
      
      let errorMessage = 'Erro ao alterar status do usuário. Tente novamente.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showNotificationModal('error', 'Erro ao Alterar Status', errorMessage);
    } finally {
      setLoadingStatusOperation(false);
    }
  }, [userForStatus, newStatus, showNotificationModal, loadData]);

  const handleCloseStatusModal = useCallback(() => {
    setShowStatusModal(false);
    setUserForStatus(null);
    setNewStatus('');
  }, []);

  // Funções auxiliares memoizadas
  const getUserHotels = useCallback((userId) => {
    return userHotels.filter(uh => uh.userId === userId);
  }, [userHotels]);

  const getHotelName = useCallback((hotelId) => {
    const hotel = hotels.find(h => h.id === hotelId);
    return hotel ? hotel.name : 'Hotel não encontrado';
  }, [hotels]);

  const getUserName = useCallback((userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Usuário não encontrado';
  }, [users]);

  // Cálculos memoizados para estatísticas
  const statsData = useMemo(() => {
    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.status !== 'INACTIVE').length;
    const adminUsers = users.filter(u => u.role === 'ADMIN').length;
    const staffUsers = users.filter(u => u.role === 'STAFF').length;
    const managerUsers = users.filter(u => u.role === 'MANAGER').length;
    
    const usersWithHotels = users.filter(user => {
    const userRelations = userHotels.filter(uh => uh.userId === user.id);
      return userRelations.length > 0;
    }).length;

    return {
      totalUsers,
      activeUsers,
      adminUsers,
      staffUsers,
      managerUsers,
      usersWithHotels,
      usersWithoutHotels: totalUsers - usersWithHotels
    };
  }, [users, userHotels]);

  // Opções para filtros
  const roleOptions = useMemo(() => [
    { value: '', label: 'Todas as Funções' },
    { value: 'ADMIN', label: 'Administrador' },
    { value: 'MANAGER', label: 'Gerente' },
    { value: 'STAFF', label: 'Funcionário' }
  ], []);

  const hotelOptions = useMemo(() => [
    { value: '', label: 'Todos os Hotéis' },
    ...hotels.map(hotel => ({
      value: hotel.id,
      label: hotel.name
    }))
  ], [hotels]);

  // Verificar se há filtros ativos
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => value && value.length > 0);
  }, [filters]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          onCreateReservation={() => {}} 
          onMenuClick={() => setIsMobileMenuOpen(true)}
        />
        <Sidebar 
          isOpen={isMobileMenuOpen} 
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <main className="lg:ml-60 pt-16">
          <div className="p-6">
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onCreateReservation={() => {}} 
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />
      <Sidebar 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)}
      />
      
      <main className="lg:ml-60 pt-16">
        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-foreground">Gestão de Usuários</h1>
                  {selectedHotel && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      {selectedHotel.name}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-sm max-w-2xl">
                  Gerencie usuários, permissões e acesso aos hotéis do sistema
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-3">
                {/* Quick Actions */}
                <div className="flex items-center bg-muted/30 rounded-lg p-1">
                  <button
                    onClick={handleClearFilters}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                    title="Limpar Filtros"
                    disabled={!hasActiveFilters}
                  >
                    <Icon name="RotateCcw" size={16} />
                    <span className="hidden sm:inline">Limpar</span>
                  </button>
            </div>

                <button
                  onClick={handleCreateModal}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                >
                  <Icon name="Plus" size={18} />
                  <span>Novo Usuário</span>
                </button>
              </div>
            </div>

            {/* Main Stats */}
            <div className="mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {(() => {
                  const stats = [
                    {
                      title: 'Total de Usuários',
                      value: statsData.totalUsers,
                      icon: 'Users',
                      color: 'primary',
                      trend: '+8%',
                      description: `${statsData.activeUsers} ativos`
                    },
                    {
                      title: 'Administradores',
                      value: statsData.adminUsers,
                      icon: 'Shield',
                      color: 'destructive',
                      trend: '+2%',
                      description: 'Acesso total'
                    },
                    {
                      title: 'Funcionários',
                      value: statsData.staffUsers,
                      icon: 'User',
                      color: 'success',
                      trend: '+12%',
                      description: 'Acesso limitado'
                    },
                    {
                      title: 'Com Acesso a Hotéis',
                      value: statsData.usersWithHotels,
                      icon: 'Building2',
                      color: 'warning',
                      trend: '+5%',
                      description: `${statsData.usersWithoutHotels} sem acesso`
                    }
                  ];

                  return stats.map((stat, index) => (
                    <div 
                      key={index} 
                      className="group relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                    >
                      <div className={`absolute top-0 right-0 w-32 h-32 bg-${stat.color}/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity`}></div>
                      
                      <div className="relative">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`p-2.5 rounded-xl bg-${stat.color}/10 group-hover:scale-110 transition-transform duration-300`}>
                            <Icon name={stat.icon} size={22} className={`text-${stat.color}`} />
                          </div>
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-success/10">
                            <Icon name="TrendingUp" size={12} className="text-success" />
                            <span className="text-xs font-semibold text-success">{stat.trend}</span>
                          </div>
                        </div>
                        
                  <div>
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                            {stat.title}
                          </h3>
                          <p className="text-2xl font-bold text-foreground">
                            {stat.value}
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed pt-1 border-t border-border/50">
                            {stat.description}
                          </p>
                        </div>
                        
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Filters */}
            <div className="mb-6">
              <div className="group relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity"></div>
                
                <div className="relative space-y-5">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon name="Filter" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">Filtros Avançados</h3>
                        <p className="text-sm text-muted-foreground">Refine sua busca de usuários</p>
                      </div>
                    </div>
                    
                    {hasActiveFilters && (
                      <button
                        onClick={handleClearFilters}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-destructive/10 text-destructive font-medium rounded-lg border border-destructive/20 hover:bg-destructive/20 transition-all"
                      >
                        <Icon name="X" size={16} />
                        <span className="text-sm">Limpar</span>
                      </button>
                    )}
                  </div>

                  {/* Search */}
                  <div className="relative">
                    <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Buscar por nome ou email..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full pl-10 hover:border-primary/30 transition-all duration-200"
                    />
                  </div>

                  {/* Filters Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Role Filter */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <Icon name="Shield" size={14} />
                        Função
                    </label>
                    <select
                        value={filters.role}
                        onChange={(e) => handleFilterChange('role', e.target.value)}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary hover:border-primary/30 transition-all duration-200"
                      >
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                    {/* Hotel Filter */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <Icon name="Building2" size={14} />
                      Hotel
                    </label>
                    <select
                        value={filters.hotel}
                        onChange={(e) => handleFilterChange('hotel', e.target.value)}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary hover:border-primary/30 transition-all duration-200"
                      >
                        {hotelOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                    {/* Status Filter */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <Icon name="Activity" size={14} />
                        Status
                    </label>
                      <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="w-full px-3 py-2.5 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary hover:border-primary/30 transition-all duration-200"
                      >
                        <option value="">Todos os Status</option>
                        <option value="ACTIVE">Ativo</option>
                        <option value="INACTIVE">Inativo</option>
                      </select>
                  </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Summary */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 p-4 bg-gradient-to-r from-muted/20 to-muted/10 rounded-lg border border-border/50">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Icon name="Search" size={18} className="text-primary" />
                  <p className="text-sm font-medium text-foreground">
                    Mostrando <span className="text-primary font-bold">{filteredUsers.length}</span> de <span className="font-bold">{users.length}</span> usuários
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Ordenar por:</span>
                <select className="text-sm border border-border rounded-lg px-3 py-1.5 bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary hover:border-primary/30 transition-all">
                  <option value="name">Nome</option>
                  <option value="email">Email</option>
                  <option value="role">Função</option>
                  <option value="createdAt">Data de criação</option>
                </select>
              </div>
            </div>

            {/* Users Table */}
            {filteredUsers.length > 0 ? (
              <>
                <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto" data-testid="user-list">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left p-4 font-semibold text-foreground">Usuário</th>
                          <th className="text-left p-4 font-semibold text-foreground">Função</th>
                          <th className="text-left p-4 font-semibold text-foreground">Hotéis</th>
                          <th className="text-left p-4 font-semibold text-foreground">Status</th>
                          <th className="text-center p-4 font-semibold text-foreground">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user) => {
                          const userHotelRelations = getUserHotels(user.id);
                          
                          return (
                            <tr key={user.id} className="border-b border-border hover:bg-muted/30 transition-smooth" data-user-id={user.id}>
                              <td className="p-4">
                                <div>
                                  <div className="font-medium text-foreground">{user.name}</div>
                                  <div className="text-sm text-muted-foreground">{user.email}</div>
                                </div>
                              </td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  user.role === 'ADMIN' ? 'bg-destructive/10 text-destructive' :
                                  user.role === 'MANAGER' ? 'bg-warning/10 text-warning' :
                                  'bg-success/10 text-success'
                                }`}>
                                  {user.role === 'ADMIN' ? 'Administrador' :
                                   user.role === 'MANAGER' ? 'Gerente' :
                                   'Funcionário'}
                                </span>
                              </td>
                              <td className="p-4">
                                {userHotelRelations.length > 0 ? (
                                  <div className="space-y-1">
                                    {userHotelRelations.slice(0, 2).map(relation => (
                                      <div key={relation.id} className="flex items-center gap-2">
                                        <Icon name="Building2" size={14} className="text-primary" />
                                        <span className="text-sm text-foreground">
                                          {relation.hotel?.name || getHotelName(relation.hotelId)}
                                        </span>
                                        {relation.isPrimary && (
                                          <span className="px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded">
                                            Principal
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                    {userHotelRelations.length > 2 && (
                                      <span className="text-xs text-muted-foreground">
                                        +{userHotelRelations.length - 2} mais
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-muted-foreground">Nenhum hotel</span>
                                )}
                              </td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  user.status !== 'INACTIVE' ? 'bg-success/10 text-success' : 'bg-muted/10 text-muted-foreground'
                                }`}>
                                  {user.status !== 'INACTIVE' ? 'Ativo' : 'Inativo'}
                                </span>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center justify-center space-x-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleEditUser(user)}
                                    title="Editar usuário"
                                  >
                                    <Icon name="Edit" size={16} color="var(--color-muted-foreground)" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleManageHotels(user)}
                                    title="Gerenciar hotéis"
                                  >
                                    <Icon name="Building2" size={16} color="var(--color-primary)" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleChangeStatus(user)}
                                    title={`${user.status !== 'INACTIVE' ? 'Desativar' : 'Ativar'} usuário`}
                                  >
                                    <Icon 
                                      name={user.status !== 'INACTIVE' ? 'UserX' : 'UserCheck'} 
                                      size={16} 
                                      color={user.status !== 'INACTIVE' ? 'var(--color-warning)' : 'var(--color-success)'} 
                                    />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteUser(user)}
                                    title="Excluir usuário"
                                  >
                                    <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 p-4 bg-gradient-to-r from-muted/20 to-muted/10 rounded-lg border border-border/50">
                  <div className="text-sm text-muted-foreground">
                    Página 1 de 1 • {filteredUsers.length} usuários
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled
                      title="Página anterior"
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>
                    <button className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground font-medium">
                      1
                    </button>
                    <button 
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled
                      title="Próxima página"
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-12 text-center overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-32 -mt-32"></div>
                <div className="relative">
                  <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Users" size={40} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Nenhum usuário encontrado</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Tente ajustar os filtros de busca ou crie um novo usuário para começar
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <button
                      onClick={handleClearFilters}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-card text-foreground font-medium rounded-lg border border-border hover:bg-accent transition-all"
                    >
                      <Icon name="RotateCcw" size={18} />
                      <span>Limpar Filtros</span>
                    </button>
                    <button
                      onClick={handleCreateModal}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all shadow-sm"
                    >
                      <Icon name="Plus" size={18} />
                      <span>Novo Usuário</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal de Edição */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Editar Usuário</h2>
              <button
                onClick={handleCloseEditModal}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Input
                  label="Nome"
                value={editingUser.name}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  placeholder="Nome completo"
                />
                
                <Input
                  label="E-mail"
                  type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
                
                <Input
                label="Nova Senha (opcional)"
                  type="password"
                value={editingUser.password}
                onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                placeholder="Deixe em branco para manter a atual"
                />
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Função
                  </label>
                  <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="STAFF">Staff</option>
                    <option value="MANAGER">Manager</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
              </div>

            <div className="flex space-x-4">
              <Button
                onClick={handleUpdateUser}
                variant="default"
                disabled={!editingUser.name || !editingUser.email || loadingStates.edit}
                className="flex-1"
              >
                {loadingStates.edit ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                    Atualizando...
                  </>
                ) : (
                  'Atualizar Usuário'
                )}
              </Button>
              <Button
                onClick={handleCloseEditModal}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Criar Novo Usuário</h2>
              <button
                onClick={handleCloseCreateModal}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <Input
                    label="Nome"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Nome completo"
                  />
                  
                  <Input
                    label="E-mail"
                    type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="email@exemplo.com"
                  />
                  
                  <Input
                label="Senha"
                    type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Senha"
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Função
                    </label>
                    <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="STAFF">Staff</option>
                      <option value="MANAGER">Manager</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                  </div>
                </div>

                {/* Campo de Seleção de Hotel Avançado */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Hotel <span className="text-destructive">*</span>
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    <div className="relative">
                      <input
                        type="text"
                        value={hotelSearchTerm}
                        onChange={(e) => handleHotelSearchChange(e.target.value)}
                        onFocus={() => setShowHotelDropdown(true)}
                        placeholder="Digite para buscar hotel..."
                        className="w-full px-3 py-2 pr-10 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        required
                        autoComplete="off"
                        maxLength={100}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <Icon name="Search" size={16} className="text-muted-foreground" />
                      </div>
                    </div>
                    
                    {/* Dropdown de Hotéis */}
                    {showHotelDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredHotels.length > 0 ? (
                          filteredHotels.map((hotel) => (
                            <div
                              key={hotel.id}
                              onClick={() => handleHotelSelect(hotel)}
                              className="px-3 py-2 hover:bg-muted cursor-pointer transition-colors border-b border-border last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium text-foreground">{hotel.name}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {hotel.code} • {hotel.city}, {hotel.state}
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                  {hotel.code}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-4 text-center text-muted-foreground">
                            <Icon name="Search" size={20} className="mx-auto mb-2 opacity-50" />
                            <p>Nenhum hotel encontrado</p>
                            <p className="text-xs">Tente outro termo de busca</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Informações de ajuda */}
                  {!newUser.hotelCode ? (
                    <p className="text-sm text-muted-foreground mt-1">
                      <Icon name="Info" size={14} className="inline mr-1" />
                      Digite o nome, código ou localização do hotel
                    </p>
                  ) : selectedHotelForForm ? (
                    <div className="mt-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="CheckCircle" size={16} className="text-primary" />
                        <span className="text-sm text-primary font-medium">
                          Hotel selecionado: {selectedHotelForForm.name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Código: {selectedHotelForForm.code} • {selectedHotelForForm.city}, {selectedHotelForForm.state}
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className="flex space-x-4">
                  <Button
                onClick={handleCreateUser}
                    variant="default"
                disabled={!newUser.name || !newUser.email || !newUser.password || !newUser.hotelCode || loadingStates.create}
                className="flex-1"
              >
                {loadingStates.create ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                    Criando...
                  </>
                ) : (
                  'Criar Usuário'
                )}
                  </Button>
                  <Button
                onClick={handleCloseCreateModal}
                    variant="outline"
                className="flex-1"
                  >
                    Cancelar
                  </Button>
            </div>
                </div>
              </div>
            )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Confirmar Exclusão</h2>
              </div>
              
            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja excluir o usuário <strong>{userToDelete.name}</strong>? 
              Esta ação não pode ser desfeita.
            </p>

            <div className="flex space-x-4">
                          <Button
                onClick={confirmDeleteUser}
                variant="destructive"
                disabled={loadingStates.delete}
                className="flex-1"
              >
                {loadingStates.delete ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                    Excluindo...
                  </>
                ) : (
                  'Excluir Usuário'
                )}
                          </Button>
                          <Button
                onClick={handleCancelDelete}
                            variant="outline"
                className="flex-1"
                          >
                Cancelar
                          </Button>
                        </div>
                      </div>
        </div>
      )}

      {/* Modal de Gestão de Hotéis */}
      {showHotelModal && userForHotel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Gerenciar Hotéis - {userForHotel.name}</h2>
              <button
                onClick={handleCloseHotelModal}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>
            
            {/* Hotéis Atuais */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Hotéis Atuais</h3>
              {getUserHotels(userForHotel.id).length > 0 ? (
                <div className="space-y-2">
                  {getUserHotels(userForHotel.id).map(relation => (
                    <div
                      key={relation.id}
                      className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border/50"
                    >
                      <div className="flex items-center space-x-3">
                        <Icon name="Building2" size={16} className="text-primary" />
                        <span className="text-foreground">
                          {relation.hotel?.name || getHotelName(relation.hotelId)}
                        </span>
                        {relation.isPrimary && (
                          <span className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-full">
                            Principal
                          </span>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => handleRemoveUserFromHotel(userForHotel.id, relation.hotelId)}
                        variant="outline"
                        size="sm"
                        iconName="Trash2"
                        className="text-destructive hover:text-destructive"
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm p-4 bg-muted/10 rounded-lg">
                  Nenhum hotel associado a este usuário.
                </p>
              )}
            </div>

            {/* Adicionar Novo Hotel */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Adicionar Novo Hotel</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Hotel
                  </label>
                  <select
                    value={selectedHotelForUser}
                    onChange={(e) => setSelectedHotelForUser(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Selecione um hotel</option>
                    {hotels.filter(hotel => 
                      !getUserHotels(userForHotel.id).some(relation => relation.hotelId === hotel.id)
                    ).map(hotel => (
                      <option key={hotel.id} value={hotel.id}>
                        {hotel.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPrimaryHotel"
                    checked={isPrimaryHotel}
                    onChange={(e) => setIsPrimaryHotel(e.target.checked)}
                    className="mr-2 rounded border-border text-primary focus:ring-primary"
                  />
                  <label htmlFor="isPrimaryHotel" className="text-sm text-foreground">
                    Hotel Principal
                  </label>
                </div>
              </div>

              <div className="flex space-x-4">
                <Button
                  onClick={handleAddUserToHotel}
                  variant="default"
                  disabled={!selectedHotelForUser || loadingHotelOperation}
                  className="flex-1"
                >
                  {loadingHotelOperation ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      Adicionando...
                    </>
                  ) : (
                    'Adicionar Hotel'
                  )}
                </Button>
                <Button
                  onClick={handleCloseHotelModal}
                  variant="outline"
                  className="flex-1"
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Alteração de Status */}
      {showStatusModal && userForStatus && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                newStatus === 'ACTIVE' ? 'bg-success/10' : 'bg-warning/10'
              }`}>
                <Icon 
                  name={newStatus === 'ACTIVE' ? 'UserCheck' : 'UserX'} 
                  size={20} 
                  className={newStatus === 'ACTIVE' ? 'text-success' : 'text-warning'} 
                />
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                {newStatus === 'ACTIVE' ? 'Ativar Usuário' : 'Desativar Usuário'}
              </h2>
            </div>
            
            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja {newStatus === 'ACTIVE' ? 'ativar' : 'desativar'} o usuário <strong>{userForStatus.name}</strong>?
            </p>

            <div className="mb-6 p-4 bg-muted/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status atual:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  userForStatus.status !== 'INACTIVE' ? 'bg-success/10 text-success' : 'bg-muted/10 text-muted-foreground'
                }`}>
                  {userForStatus.status !== 'INACTIVE' ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-muted-foreground">Novo status:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  newStatus === 'ACTIVE' ? 'bg-success/10 text-success' : 'bg-muted/10 text-muted-foreground'
                }`}>
                  {newStatus === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>

            <div className="flex space-x-4">
              <Button
                onClick={handleConfirmStatusChange}
                variant={newStatus === 'ACTIVE' ? 'default' : 'destructive'}
                disabled={loadingStatusOperation}
                className="flex-1"
              >
                {loadingStatusOperation ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                    {newStatus === 'ACTIVE' ? 'Ativando...' : 'Desativando...'}
                  </>
                ) : (
                  `${newStatus === 'ACTIVE' ? 'Ativar' : 'Desativar'} Usuário`
                )}
              </Button>
              <Button
                onClick={handleCloseStatusModal}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Notificação */}
      {showNotification && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                notificationData.type === 'success' ? 'bg-success/10' :
                notificationData.type === 'error' ? 'bg-destructive/10' :
                'bg-primary/10'
              }`}>
                <Icon 
                  name={
                    notificationData.type === 'success' ? 'CheckCircle' :
                    notificationData.type === 'error' ? 'XCircle' :
                    'Info'
                  } 
                  size={20} 
                  className={
                    notificationData.type === 'success' ? 'text-success' :
                    notificationData.type === 'error' ? 'text-destructive' :
                    'text-primary'
                  } 
                />
              </div>
              <h2 className="text-lg font-semibold text-foreground">{notificationData.title}</h2>
            </div>
            
            <p className="text-muted-foreground mb-6">
              {notificationData.message}
            </p>

            {notificationData.details && (
              <div className="mb-6 p-4 bg-muted/20 rounded-lg">
                <h3 className="text-sm font-medium text-foreground mb-2">Detalhes:</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {notificationData.details.map((detail, index) => (
                    <li key={index}>
                      <strong>{detail.label}:</strong> {detail.value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              onClick={handleCloseNotification}
              variant="default"
              className="w-full"
            >
              Fechar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 